/**
 * My Pricing Page - For Business Users
 *
 * Shows user's pricing type and relevant info:
 * - Individual users: Standard pricing info
 * - Business users without contract: Bulk pricing info
 * - Business users with contract: Full contract details
 *
 * @module app/my-pricing/page
 */
'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Button,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  TrendingUp as GrowthIcon,
  LocalShipping as DeliveryIcon,
  Savings as SavingsIcon,
  Calculate as SimulatorIcon,
  Info as InfoIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { buildApiConfig } from '../../services/api/apiClient';
import { endPointEnums } from '../../services/api/endpoints';
import useApiCall from '../../services/api/useApiCall';
import { LayoutContainer } from '@/kit';
import { brandingConfig } from '@/config/branding';
import { useTranslation } from '@/kit/useTranslation';

const MyPricingPage = () => {
  const { makeApiCall } = useApiCall();
  const { t } = useTranslation();
  const [pricingInfo, setPricingInfo] = useState(null);
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Simulator state
  const [simItemCount, setSimItemCount] = useState(10);
  const [simDistance, setSimDistance] = useState(15);
  const [simWeight, setSimWeight] = useState(5);
  const [simResult, setSimResult] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [businessMetrics, setBusinessMetrics] = useState(null);
  const [estimateData, setEstimateData] = useState(null);
  const [showRawMetrics, setShowRawMetrics] = useState(false);

  // Compute an estimated monthly savings using a sample scenario and the contract
  const computeEstimate = useCallback(
    async (contract) => {
      try {
        const shipments = parseInt(
          contract.min_quantity_commitment || contract.minQuantityCommitment || 20
        );
        const sampleDistance = 15;
        const sampleWeight = 5;

        // Standard per-shipment price (individual pricing)
        const standardConfig = buildApiConfig(endPointEnums.CALCULATE_PRICE);
        let standardRes = null;
        try {
          standardRes = await makeApiCall(standardConfig, {
            distanceKm: sampleDistance,
            weightKg: sampleWeight,
            itemCount: 1,
            userType: 'INDIVIDUAL',
          });
        } catch (e) {
          console.warn('[MyPricing] standard price API failed, falling back to heuristic', e);
        }

        // Business per-shipment price (using contract)
        const businessConfig = buildApiConfig(endPointEnums.CALCULATE_BUSINESS_PRICE);
        let businessRes = null;
        try {
          businessRes = await makeApiCall(businessConfig, {
            contractId: contract.contract_id || contract.contractId,
            distanceKm: sampleDistance,
            actualWeightKg: sampleWeight,
          });
        } catch (e) {
          console.warn(
            '[MyPricing] business price API failed, falling back to contract heuristics',
            e
          );
        }

        let stdPrice = parseFloat(
          standardRes?.data?.finalPrice ||
            standardRes?.data?.final_price ||
            standardRes?.data?.subtotal ||
            0
        );
        let bizPrice = parseFloat(
          businessRes?.data?.finalPrice ||
            businessRes?.data?.final_price ||
            businessRes?.data?.subtotal ||
            0
        );

        // Heuristics if APIs failed or returned 0
        if (!stdPrice || stdPrice === 0) {
          const assumedDistanceRate = 5; // conservative
          const assumedWeightRate = 8; // conservative
          stdPrice = assumedWeightRate * sampleWeight + assumedDistanceRate * sampleDistance;
        }

        if (!bizPrice || bizPrice === 0) {
          // Use contract discount heuristic: apply contract base discount to std price
          const discountPercent =
            contract.base_discount_percent || contract.baseDiscountPercent || 0;
          bizPrice = Math.max(0, stdPrice * (1 - discountPercent / 100));
        }

        const perShipStandard = !isNaN(stdPrice) ? stdPrice : 0;
        const perShipBusiness = !isNaN(bizPrice) ? bizPrice : 0;
        const monthlySavings = Math.max(0, (perShipStandard - perShipBusiness) * shipments);
        const traditional = perShipStandard * shipments + monthlySavings;
        const pct = traditional > 0 ? Math.round((monthlySavings / traditional) * 100) : 0;

        setEstimateData({
          perShipStandard,
          perShipBusiness,
          shipments,
          monthlySavings,
          pct,
          breakdown: businessRes?.data?.breakdown || null,
          debug: {
            standardRes: standardRes?.data || null,
            businessRes: businessRes?.data || null,
          },
        });
      } catch (err) {
        console.error('[MyPricing] Estimate computation failed', err);
        setEstimateData(null);
      }
    },
    [makeApiCall]
  );

  const loadPricingData = useCallback(async () => {
    try {
      // Get user's pricing info first
      const pricingConfig = buildApiConfig(endPointEnums.GET_MY_PRICING_INFO);
      const pricingRes = await makeApiCall(pricingConfig);

      if (pricingRes?.success && pricingRes.data) {
        setPricingInfo(pricingRes.data);

        // If there's a contract (active or draft), fetch full details
        if (pricingRes.data.contractId) {
          const contractConfig = buildApiConfig(endPointEnums.GET_MY_CONTRACT);
          const contractRes = await makeApiCall(contractConfig);
          if (contractRes?.success) {
            setContract(contractRes.data);
            // Fetch current month business metrics for savings display (fetch regardless of active)
            let metricsRes = null;
            try {
              const metricsConfig = buildApiConfig(endPointEnums.GET_CURRENT_MONTH_METRICS);
              metricsRes = await makeApiCall(metricsConfig);
              if (metricsRes?.success && metricsRes.data) {
                setBusinessMetrics(metricsRes.data);
              } else {
                setBusinessMetrics(null);
              }
            } catch (metricsErr) {
              console.error('[MyPricing] Failed to fetch business metrics', metricsErr);
              setBusinessMetrics(null);
              metricsRes = null;
            }

            // If metrics missing or show zero savings, compute an estimate fallback
            const savings = metricsRes?.data?.total_savings || metricsRes?.data?.totalSavings || 0;
            if (!metricsRes || savings === 0) {
              computeEstimate(contractRes.data);
            }
          }
        }
      }
    } catch (err) {
      console.error('[MyPricing] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [makeApiCall, computeEstimate]);

  useEffect(() => {
    loadPricingData();
  }, [loadPricingData]);

  const formatCurrency = (amount) => `₹${parseFloat(amount || 0).toFixed(2)}`;
  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : '-';

  const runSimulation = async () => {
    setSimulating(true);
    try {
      const simConfig = buildApiConfig(endPointEnums.CALCULATE_PRICE);
      const payload = {
        itemCount: parseInt(simItemCount),
        distanceKm: parseFloat(simDistance),
        weightKg: parseFloat(simWeight),
      };
      const res = await makeApiCall(simConfig, payload);
      if (res?.success) {
        // Normalize the response: calculate missing totals from breakdown if needed
        const data = res.data;
        const normalized = normalizeSimulationResponse(data);
        setSimResult(normalized);
      }
    } catch (err) {
      console.error('[MyPricing] Simulation error:', err);
    } finally {
      setSimulating(false);
    }
  };

  /**
   * Normalize pricing response: if finalPrice/subtotal are null but breakdown exists,
   * calculate them from the breakdown components
   */
  const normalizeSimulationResponse = (data) => {
    if (!data) return data;

    // If totals are already present, return as-is
    if (data.finalPrice !== null && data.subtotal !== null && data.taxAmount !== null) {
      return data;
    }

    // Extract breakdown components
    const breakdown = data.breakdown || {};
    const distanceCost = parseFloat(breakdown.distanceCost) || 0;
    const weightCost = parseFloat(breakdown.weightCost) || 0;
    const basePrice = parseFloat(breakdown.basePrice) || 0;
    const taperingSavings = parseFloat(breakdown.taperingSavings) || 0;
    const volumeRebate = parseFloat(breakdown.volumeRebate) || 0;
    const commitmentAdjustmentAmount = breakdown.commitmentAdjustment?.amount
      ? parseFloat(breakdown.commitmentAdjustment.amount)
      : 0;

    // Calculate subtotal: sum of all charges
    const calculatedSubtotal =
      distanceCost +
      weightCost +
      basePrice -
      taperingSavings -
      volumeRebate -
      commitmentAdjustmentAmount;

    // Determine GST percent from backend (do NOT default in UI)
    const gstPercentRaw = breakdown.gstPercent ?? data.gstPercent ?? null;
    const gstPercent = gstPercentRaw != null ? parseFloat(gstPercentRaw) : null;

    // Only calculate tax if GST percent is provided. If backend returned a tax amount (data.taxAmount or data.gst), prefer that.
    let calculatedTax = null;
    if (gstPercent != null) {
      calculatedTax = (calculatedSubtotal * gstPercent) / 100;
    }

    // If backend supplied tax directly, prefer it
    const backendTax =
      data.taxAmount != null
        ? parseFloat(data.taxAmount)
        : data.gst != null
          ? parseFloat(data.gst)
          : null;

    // Determine final tax to show
    const finalTax = calculatedTax != null ? calculatedTax : backendTax;

    // Calculate final price (if tax unknown, use subtotal as finalPrice but leave tax null)
    const calculatedFinalPrice =
      finalTax != null ? calculatedSubtotal + finalTax : calculatedSubtotal;

    // Log a warning so we can track missing GST configurations in backend (useful for analytics)
    if (gstPercent == null && backendTax == null) {
      console.warn(
        '[MyPricing] GST not provided by backend for simulation. Backend should set GST in admin settings.'
      );
    }

    // Ensure breakdown contains gst fields so UI can read them where it expects
    const normalizedBreakdown = {
      ...breakdown,
      gst: breakdown.gst ?? (finalTax != null ? Math.max(0, finalTax) : null),
      gstPercent: breakdown.gstPercent ?? (gstPercent != null ? gstPercent : null),
      subtotal: breakdown.subtotal ?? Math.max(0, calculatedSubtotal),
    };

    // Return normalized data
    return {
      ...data,
      breakdown: normalizedBreakdown,
      subtotal: data.subtotal ?? normalizedBreakdown.subtotal,
      taxAmount: data.taxAmount ?? normalizedBreakdown.gst ?? null,
      finalPrice:
        data.finalPrice ??
        (calculatedFinalPrice != null ? Math.max(0, calculatedFinalPrice) : null),
      gst: data.gst ?? normalizedBreakdown.gst ?? null,
      total:
        data.total ?? (calculatedFinalPrice != null ? Math.max(0, calculatedFinalPrice) : null),
    };
  };

  if (loading) {
    return (
      <LayoutContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      </LayoutContainer>
    );
  }

  const pricingType = pricingInfo?.pricingType || 'STANDARD';

  return (
    <LayoutContainer>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            fontWeight={700}
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '1.95rem' } }}
          >
            {t('myPricing.pageTitle')}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.92rem', sm: '1rem' } }}
          >
            {pricingInfo?.description || t('myPricing.defaultDescription')}
          </Typography>
        </Box>

        {/* Pricing Type Badge */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 0.75, sm: 2 },
            mb: 2,
            border: '1px solid #e2e8f0',
            borderRadius: 1.5,
            bgcolor:
              pricingType === 'BUSINESS'
                ? '#ecfdf5'
                : pricingType === 'BULK'
                  ? '#fef3c7'
                  : '#f0f9ff',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'row' },
              alignItems: 'center',
              gap: 1.5,
              justifyContent: 'space-between',
            }}
          >
            {pricingType === 'BUSINESS' ? (
              <BusinessIcon
                sx={{
                  fontSize: { xs: 24, sm: 32 },
                  color: '#059669',
                  flexShrink: 0,
                }}
              />
            ) : pricingType === 'BULK' ? (
              <DeliveryIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: '#d97706' }} />
            ) : (
              <PersonIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: '#0284c7' }} />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  fontSize: { xs: '0.95rem', sm: '1.1rem' },
                  lineHeight: 1.1,
                }}
              >
                {pricingType === 'BUSINESS'
                  ? t('myPricing.businessPricing')
                  : pricingType === 'BULK'
                    ? t('myPricing.bulkPricing')
                    : t('myPricing.standardPricing')}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  color: 'text.secondary',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {pricingType === 'BUSINESS'
                  ? t('myPricing.contractLabel', {
                      name: pricingInfo?.contractName || t('myPricing.contractActive'),
                    })
                  : pricingType === 'BULK'
                    ? t('myPricing.volumeDiscounts')
                    : t('myPricing.weightDistanceBased')}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={
                pricingType === 'BUSINESS'
                  ? contract?.status === 'DRAFT'
                    ? t('myPricing.pending')
                    : t('myPricing.active')
                  : pricingType === 'BULK'
                    ? t('myPricing.eligible')
                    : t('myPricing.standard')
              }
              color={
                pricingType === 'BUSINESS'
                  ? contract?.status === 'DRAFT'
                    ? 'warning'
                    : 'success'
                  : pricingType === 'BULK'
                    ? 'warning'
                    : 'info'
              }
              sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, height: 24 }}
            />
          </Box>
        </Paper>

        {/* Tabs */}
        <Paper sx={{ borderRadius: 2, mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab
              value="overview"
              label={t('myPricing.tabOverview')}
              icon={<InfoIcon sx={{ fontSize: 16 }} />}
              iconPosition="start"
              sx={{
                textTransform: 'none',
                minHeight: 40,
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                px: 1.5,
              }}
            />
            <Tab
              value="simulator"
              label={t('myPricing.tabPriceCalculator')}
              icon={<SimulatorIcon sx={{ fontSize: 16 }} />}
              iconPosition="start"
              sx={{
                textTransform: 'none',
                minHeight: 40,
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                px: 1.5,
              }}
            />
            {pricingType === 'BUSINESS' && (
              <Tab
                value="contract"
                label={t('myPricing.tabContractDetails')}
                icon={<BusinessIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                sx={{ textTransform: 'none' }}
              />
            )}
          </Tabs>
        </Paper>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <Grid container spacing={3}>
            {/* How Your Pricing Works */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  gutterBottom
                  sx={{ fontSize: { xs: '1.05rem', sm: '1.125rem' } }}
                >
                  {t('myPricing.howPricingWorks')}
                </Typography>

                {pricingType === 'STANDARD' && (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t('myPricing.standardAlert')}
                    </Alert>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={6} md={3}>
                        <Card variant="outlined" sx={{ borderRadius: 2, minHeight: 92 }}>
                          <CardContent>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {t('myPricing.baseRate')}
                            </Typography>
                            <Typography variant="h6">₹8/kg</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={6} md={3}>
                        <Card variant="outlined" sx={{ borderRadius: 2, minHeight: 92 }}>
                          <CardContent>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {t('myPricing.distanceRate')}
                            </Typography>
                            <Typography variant="h6">₹5-8/km</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={6} md={3}>
                        <Card variant="outlined" sx={{ borderRadius: 2, minHeight: 92 }}>
                          <CardContent>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {t('myPricing.gst')}
                            </Typography>
                            <Typography variant="h6">18%</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={6} md={3}>
                        <Card variant="outlined" sx={{ borderRadius: 2, minHeight: 92 }}>
                          <CardContent>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {t('myPricing.serviceFee')}
                            </Typography>
                            <Typography variant="h6">₹25</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {pricingType === 'BULK' && (
                  <Box>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {t('myPricing.bulkAlert')}
                    </Alert>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {t('myPricing.discountTiers')}
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>{t('myPricing.items5to10')}</TableCell>
                            <TableCell align="right">
                              ₹50/{t('myPricing.perItem', { price: '' }).trim()}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>
                              {t('myPricing.percentOff', { percent: 0 })}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>{t('myPricing.items11to25')}</TableCell>
                            <TableCell align="right">
                              ₹48/{t('myPricing.perItem', { price: '' }).trim()}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>
                              {t('myPricing.percentOff', { percent: 5 })}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>{t('myPricing.items26to50')}</TableCell>
                            <TableCell align="right">
                              ₹45/{t('myPricing.perItem', { price: '' }).trim()}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>
                              {t('myPricing.percentOff', { percent: 10 })}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>{t('myPricing.items51to100')}</TableCell>
                            <TableCell align="right">
                              ₹42/{t('myPricing.perItem', { price: '' }).trim()}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>
                              {t('myPricing.percentOff', { percent: 15 })}
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ bgcolor: '#ecfdf5' }}>
                            <TableCell>{t('myPricing.items100plus')}</TableCell>
                            <TableCell align="right">
                              ₹40/{t('myPricing.perItem', { price: '' }).trim()}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: 'success.main', fontWeight: 700 }}
                            >
                              {t('myPricing.percentOff', { percent: 20 })}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Want even better rates? <strong>Contact us for a custom contract</strong> with
                      commitment-based discounts, rebates, and waived fees!
                    </Alert>
                  </Box>
                )}

                {pricingType === 'BUSINESS' && contract && (
                  <Box>
                    {contract.status === 'DRAFT' ? (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                          {t('myPricing.contractPendingTitle')}
                        </Typography>
                        <Typography variant="body2">
                          {t('myPricing.contractPendingDescription')}
                        </Typography>
                      </Alert>
                    ) : (
                      <Alert
                        severity="success"
                        icon={<CheckIcon />}
                        sx={{ mb: 2, p: { xs: 1, sm: 2 }, borderRadius: 1 }}
                      >
                        {t('myPricing.contractActiveAlert')}
                      </Alert>
                    )}
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={6} md={3}>
                        <Card
                          variant="outlined"
                          sx={{
                            bgcolor: '#ecfdf5',
                            borderRadius: 2,
                            minHeight: 80,
                          }}
                        >
                          <CardContent sx={{ py: 1.25 }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              sx={{ fontSize: { xs: '0.78rem', sm: '0.9rem' } }}
                            >
                              {t('myPricing.baseDiscount')}
                            </Typography>
                            <Typography
                              variant="h5"
                              color="success.main"
                              fontWeight={700}
                              sx={{
                                fontSize: { xs: '1.15rem', sm: '1.25rem' },
                              }}
                            >
                              {contract.base_discount_percent || contract.baseDiscountPercent || 0}%
                              {t('myPricing.off')}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={6} md={3}>
                        <Card variant="outlined" sx={{ borderRadius: 2, minHeight: 92 }}>
                          <CardContent>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {t('myPricing.ratePerKg')}
                            </Typography>
                            <Typography variant="h5">
                              {formatCurrency(
                                contract.base_rate_per_kg || contract.baseRatePerKg || 8
                              )}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={6} md={3}>
                        <Card
                          variant="outlined"
                          sx={{
                            bgcolor: '#ecfdf5',
                            borderRadius: 2,
                            minHeight: 92,
                          }}
                        >
                          <CardContent>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {t('myPricing.feesWaived')}
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color="success.main">
                              {t('myPricing.feesWaivedValue')}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} sm={6} md={3}>
                        <Card variant="outlined" sx={{ borderRadius: 2, minHeight: 92 }}>
                          <CardContent>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {t('myPricing.validUntil')}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {formatDate(contract.end_date || contract.endDate)}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Simulator Tab */}
        {activeTab === 'simulator' && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              border: '1px solid #e2e8f0',
              borderRadius: 2,
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              gutterBottom
              sx={{ fontSize: { xs: '1.05rem', sm: '1.125rem' } }}
            >
              🧮 Price Calculator
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Calculate your delivery cost with your current pricing plan
            </Typography>

            <Grid container spacing={3}>
              {/* Input Card */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, sm: 3.5 },
                    borderRadius: 3,
                    bgcolor: '#fff',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                    },
                  }}
                >
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      gutterBottom
                      sx={{
                        fontSize: { xs: '1.05rem', sm: '1.15rem' },
                        color: '#0f172a',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ fontSize: '1.4rem' }}>📊</Box>
                      {t('myPricing.enterDetails')}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#64748b', display: 'block', mt: 0.5 }}
                    >
                      {t('myPricing.enterDetailsDescription')}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2.5,
                      mb: 3,
                    }}
                  >
                    {pricingType !== 'STANDARD' && (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{
                            fontSize: '0.75rem',
                            color: '#475569',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'block',
                            mb: 1,
                          }}
                        >
                          Number of Items
                        </Typography>
                        <TextField
                          type="number"
                          value={simItemCount}
                          onChange={(e) => setSimItemCount(e.target.value)}
                          disabled={simulating}
                          size="small"
                          fullWidth
                          inputProps={{ min: 1 }}
                          placeholder="e.g., 10"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              bgcolor: '#fff',
                              fontSize: '1rem',
                              fontWeight: 500,
                              // border: '1.5px solid #e2e8f0',
                              transition: 'all 0.2s ease',
                              '&:hover': { borderColor: '#cbd5e1' },
                              '&.Mui-focused': {
                                borderColor: 'success.main',
                                boxShadow: '0 0 0 1px rgba(5, 150, 105, 0.1)',
                              },
                              '&.Mui-disabled': {
                                bgcolor: '#f1f5f9',
                                color: '#94a3b8',
                              },
                            },
                            '& .MuiOutlinedInput-input': { p: '10px 12px' },
                          }}
                        />
                      </Box>
                    )}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{
                          fontSize: '0.75rem',
                          color: '#475569',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          display: 'block',
                          mb: 1,
                        }}
                      >
                        {t('myPricing.distanceKm')}
                      </Typography>
                      <TextField
                        type="number"
                        value={simDistance}
                        onChange={(e) => setSimDistance(e.target.value)}
                        disabled={simulating}
                        size="small"
                        fullWidth
                        inputProps={{ min: 0, step: 0.5 }}
                        placeholder="e.g., 15"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            bgcolor: '#fff',
                            fontSize: '1rem',
                            fontWeight: 500,
                            // border: '1.5px solid #e2e8f0',
                            transition: 'all 0.2s ease',
                            '&:hover': { borderColor: '#cbd5e1' },
                            '&.Mui-focused': {
                              borderColor: 'success.main',
                              boxShadow: '0 0 0 1px rgba(5, 150, 105, 0.1)',
                            },
                            '&.Mui-disabled': {
                              bgcolor: '#f1f5f9',
                              color: '#94a3b8',
                            },
                          },
                          '& .MuiOutlinedInput-input': { p: '10px 12px' },
                        }}
                      />
                    </Box>
                    {pricingType === 'STANDARD' && (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{
                            fontSize: '0.75rem',
                            color: '#475569',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'block',
                            mb: 1,
                          }}
                        >
                          {t('myPricing.weightKg')}
                        </Typography>
                        <TextField
                          type="number"
                          value={simWeight}
                          onChange={(e) => setSimWeight(e.target.value)}
                          disabled={simulating}
                          size="small"
                          fullWidth
                          inputProps={{ min: 0.1, step: 0.5 }}
                          placeholder="e.g., 5"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              bgcolor: '#fff',
                              fontSize: '1rem',
                              fontWeight: 500,
                              border: '1.5px solid #e2e8f0',
                              transition: 'all 0.2s ease',
                              '&:hover': { borderColor: '#cbd5e1' },
                              '&.Mui-focused': {
                                borderColor: 'success.main',
                                boxShadow: '0 0 0 3px rgba(5, 150, 105, 0.1)',
                              },
                              '&.Mui-disabled': {
                                bgcolor: '#f1f5f9',
                                color: '#94a3b8',
                              },
                            },
                            '& .MuiOutlinedInput-input': { p: '10px 12px' },
                          }}
                        />
                      </Box>
                    )}
                    <Button
                      variant="contained"
                      color="success"
                      onClick={runSimulation}
                      disabled={simulating}
                      startIcon={
                        simulating ? (
                          <CircularProgress size={18} sx={{ color: 'inherit' }} />
                        ) : (
                          <SimulatorIcon />
                        )
                      }
                      fullWidth
                      sx={{
                        height: 50,
                        borderRadius: 2,
                        fontWeight: 700,
                        fontSize: '1rem',
                        textTransform: 'none',
                        boxShadow: '0 4px 14px rgba(5, 150, 105, 0.25)',
                        bgcolor: 'success.main',
                        '&:hover:not(:disabled)': {
                          bgcolor: 'success.dark',
                          boxShadow: '0 6px 20px rgba(5, 150, 105, 0.35)',
                          transform: 'translateY(-1px)',
                        },
                        '&:disabled': {
                          bgcolor: '#cbd5e1',
                          color: '#94a3b8',
                          boxShadow: 'none',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      {simulating ? t('myPricing.calculating') : t('myPricing.calculatePrice')}
                    </Button>
                  </Box>
                </Paper>
              </Grid>

              {/* Result Card */}
              <Grid item xs={12}>
                {simResult ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2.5 },
                      borderRadius: 3,
                      bgcolor: '#fff',
                      // border: '1px solid #e2e8f0',
                      // boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        // boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                      },
                    }}
                  >
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          mb: 2,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography
                          variant="h6"
                          fontWeight={800}
                          sx={{
                            fontSize: { xs: '1.05rem', sm: '1.15rem' },
                            color: '#0f172a',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Box sx={{ fontSize: '1.4rem' }}>💰</Box>
                          {t('myPricing.priceBreakdown')}
                        </Typography>
                        <Chip
                          label={simResult.pricingEngine || pricingType}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{
                            fontSize: '0.7rem',
                            height: 28,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#64748b',
                          display: 'block',
                        }}
                      >
                        {t('myPricing.detailedCostCalc')}
                      </Typography>
                    </Box>

                    {simResult.breakdown?.itemDiscount > 0 && (
                      <Alert
                        icon={<Box sx={{ fontSize: '1.3rem', mb: 0.2 }}>✨</Box>}
                        severity="success"
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          bgcolor: '#ecfdf5',
                          borderLeft: '4px solid',
                          borderColor: 'success.main',
                          '& .MuiAlert-icon': {
                            color: 'success.main',
                            mr: 1.5,
                          },
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{
                              color: 'success.dark',
                              mb: 0.5,
                              fontSize: '0.9rem',
                            }}
                          >
                            Contract Benefits Applied
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'success.dark', fontSize: '0.75rem' }}
                          >
                            You&apos;re saving{' '}
                            <strong>{formatCurrency(simResult.breakdown.itemDiscount)}</strong> with
                            your business contract!
                          </Typography>
                        </Box>
                      </Alert>
                    )}

                    {simResult.breakdown?.taperingSavings > 0 && (
                      <Alert
                        icon={<Box sx={{ fontSize: '1.3rem', mb: 0.2 }}>🎉</Box>}
                        severity="success"
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          bgcolor: '#ecfdf5',
                          borderLeft: '4px solid',
                          borderColor: 'success.main',
                          '& .MuiAlert-icon': {
                            color: 'success.main',
                            mr: 1.5,
                          },
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{
                              color: 'success.dark',
                              mb: 0.5,
                              fontSize: '0.9rem',
                            }}
                          >
                            {t('myPricing.volumeSavingsApplied')}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'success.dark', fontSize: '0.75rem' }}
                          >
                            {t('myPricing.savingWithVolume', {
                              amount: formatCurrency(simResult.breakdown.taperingSavings),
                            })}
                          </Typography>
                        </Box>
                      </Alert>
                    )}

                    <Box
                      sx={{
                        bgcolor: '#f8fafc',
                        p: 2.5,
                        borderRadius: 2.5,
                        border: '1.5px solid #e2e8f0',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {/* Warn if GST is not configured in backend */}
                      {!simResult.breakdown?.gstPercent &&
                        !simResult.gst &&
                        !simResult.taxAmount && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            <strong>{t('myPricing.gstNotConfigured')}</strong>.{' '}
                            {t('myPricing.gstNotConfiguredDescription')}
                          </Alert>
                        )}

                      <TableContainer>
                        <Table>
                          <TableBody>
                            {/* Distance Charge */}
                            {(simResult.breakdown?.distanceCost !== null ||
                              simResult.breakdown?.distanceCost !== undefined) && (
                              <TableRow sx={{ borderBottom: '1px solid #e2e8f0' }}>
                                <TableCell
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    fontSize: '0.9rem',
                                    color: '#475569',
                                    fontWeight: 500,
                                  }}
                                >
                                  {t('myPricing.distanceCharge', { distance: simDistance })}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    color: '#0f172a',
                                  }}
                                >
                                  {formatCurrency(simResult.breakdown?.distanceCost || 0)}
                                </TableCell>
                              </TableRow>
                            )}

                            {/* Weight Charge */}
                            {(simResult.breakdown?.weightCost !== null ||
                              simResult.breakdown?.weightCost !== undefined) && (
                              <TableRow sx={{ borderBottom: '1px solid #e2e8f0' }}>
                                <TableCell
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    fontSize: '0.9rem',
                                    color: '#475569',
                                    fontWeight: 500,
                                  }}
                                >
                                  {t('myPricing.weightCharge', { weight: simWeight })}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    color: '#0f172a',
                                  }}
                                >
                                  {formatCurrency(simResult.breakdown?.weightCost || 0)}
                                </TableCell>
                              </TableRow>
                            )}

                            {/* Base Price */}
                            {(simResult.breakdown?.basePrice !== null ||
                              simResult.breakdown?.basePrice !== undefined) && (
                              <TableRow sx={{ borderBottom: '1px solid #e2e8f0' }}>
                                <TableCell
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    fontSize: '0.9rem',
                                    color: '#475569',
                                    fontWeight: 500,
                                  }}
                                >
                                  {t('myPricing.basePrice')}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    color: '#0f172a',
                                  }}
                                >
                                  {formatCurrency(simResult.breakdown?.basePrice || 0)}
                                </TableCell>
                              </TableRow>
                            )}

                            {/* Tapering Savings - shown as discount */}
                            {simResult.breakdown?.taperingSavings > 0 && (
                              <TableRow
                                sx={{
                                  borderBottom: '1px solid #e2e8f0',
                                  bgcolor: '#f0fdf4',
                                }}
                              >
                                <TableCell
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    fontSize: '0.9rem',
                                    color: '#475569',
                                    fontWeight: 500,
                                  }}
                                >
                                  {t('myPricing.volumeSavings')}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    fontSize: '0.9rem',
                                    fontWeight: 700,
                                    color: '#059669',
                                  }}
                                >
                                  -{formatCurrency(simResult.breakdown.taperingSavings)}
                                </TableCell>
                              </TableRow>
                            )}

                            {/* Volume Rebate */}
                            {simResult.breakdown?.volumeRebate > 0 && (
                              <TableRow
                                sx={{
                                  borderBottom: '1px solid #e2e8f0',
                                  bgcolor: '#f0fdf4',
                                }}
                              >
                                <TableCell
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    fontSize: '0.9rem',
                                    color: '#475569',
                                    fontWeight: 500,
                                  }}
                                >
                                  {t('myPricing.volumeRebate')}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    fontSize: '0.9rem',
                                    fontWeight: 700,
                                    color: '#059669',
                                  }}
                                >
                                  -{formatCurrency(simResult.breakdown.volumeRebate)}
                                </TableCell>
                              </TableRow>
                            )}
                            <TableRow
                              sx={{
                                borderBottom: '2px solid',
                                borderColor: 'success.main',
                                bgcolor: '#f0fdf4',
                              }}
                            >
                              <TableCell
                                sx={{
                                  py: 1.75,
                                  px: 2,
                                  fontSize: '0.95rem',
                                  fontWeight: 700,
                                  color: 'success.dark',
                                }}
                              >
                                {t('myPricing.subtotal')}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  py: 1.75,
                                  px: 2,
                                  fontSize: '0.95rem',
                                  fontWeight: 700,
                                  color: 'success.dark',
                                }}
                              >
                                {formatCurrency(
                                  simResult.breakdown?.subtotal || simResult.subtotal || 0
                                )}
                              </TableCell>
                            </TableRow>

                            {((simResult.breakdown?.gst && simResult.breakdown.gst > 0) ||
                              simResult.gst > 0 ||
                              simResult.taxAmount > 0) && (
                              <TableRow sx={{ borderBottom: '1px solid #e2e8f0' }}>
                                <TableCell
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    fontSize: '0.9rem',
                                    color: '#475569',
                                    fontWeight: 500,
                                  }}
                                >
                                  {t('myPricing.gstPercent', {
                                    percent: simResult.breakdown?.gstPercent ?? 5,
                                  })}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    color: '#0f172a',
                                  }}
                                >
                                  {formatCurrency(
                                    simResult.breakdown?.gst ??
                                      simResult.gst ??
                                      simResult.taxAmount ??
                                      0
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                            <TableRow
                              sx={{
                                borderRadius: '0 0 8px 8px',
                                overflow: 'hidden',
                              }}
                            >
                              <TableCell
                                sx={{
                                  py: 2,
                                  px: 2,
                                  fontSize: '1.05rem',
                                  fontWeight: 800,
                                  color: '#fff',
                                  bgcolor: 'success.main',
                                  borderRadius: '0 0 0 8px',
                                }}
                              >
                                {t('myPricing.totalAmount')}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  py: 2,
                                  px: 2,
                                  fontSize: '1.1rem',
                                  fontWeight: 800,
                                  color: '#fff',
                                  bgcolor: 'success.main',
                                  borderRadius: '0 0 8px 0',
                                }}
                              >
                                {formatCurrency(simResult.total)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Paper>
                ) : (
                  <Box
                    sx={{
                      p: { xs: 4, sm: 5 },
                      textAlign: 'center',
                      minHeight: 420,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2.5,
                      bgcolor: '#f8fafc',
                      borderRadius: 3,
                      border: '2px dashed #cbd5e1',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: 80,
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box sx={{ fontSize: '4rem', opacity: 0.1 }}>📊</Box>
                      <SimulatorIcon
                        sx={{
                          fontSize: 60,
                          color: '#cbd5e1',
                          position: 'absolute',
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{ color: '#475569', mb: 1, fontSize: '1.05rem' }}
                      >
                        {t('myPricing.readyToCalculate')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#94a3b8',
                          lineHeight: 1.6,
                          maxWidth: 420,
                        }}
                      >
                        {t('myPricing.readyToCalculateDescription')}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1, opacity: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#cbd5e1', fontSize: '0.75rem' }}>
                        {t('myPricing.pricingHint')}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Contract Tab (Business users only) */}
        {activeTab === 'contract' && pricingType === 'BUSINESS' && contract && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
              alignItems: 'flex-start',
            }}
          >
            <Box sx={{ flex: { xs: 1, md: '0 0 calc(66.666% - 12px)' } }}>
              <Paper
                elevation={1}
                sx={{
                  p: 4,
                  borderRadius: 2,
                  bgcolor: contract.status === 'DRAFT' ? '#fff8e6' : '#f3fff6',
                  border: '1px solid',
                  borderColor: contract.status === 'DRAFT' ? '#fde7b9' : '#e6f7ea',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                  }}
                >
                  <Box>
                    <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
                      {contract.contract_name || contract.contractName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {contract.business_name || contract.businessName}
                    </Typography>
                  </Box>

                  <Chip
                    label={
                      contract.status === 'DRAFT'
                        ? t('myPricing.draftPending')
                        : t('myPricing.active')
                    }
                    size="small"
                    sx={{
                      textTransform: 'uppercase',
                      borderRadius: '999px',
                      px: 2,
                      py: 0.5,
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      color: contract.status === 'DRAFT' ? '#5d3a00' : '#14532d',
                      bgcolor: contract.status === 'DRAFT' ? '#fff7e6' : '#ecfdf5',
                    }}
                  />
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        bgcolor: '#ffffff',
                        border: '1px solid #eef5f7',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {t('myPricing.contractPeriod')}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatDate(contract.start_date || contract.startDate)} -{' '}
                        {formatDate(contract.end_date || contract.endDate)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        bgcolor: '#ffffff',
                        border: '1px solid #eef5f7',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {t('myPricing.baseRatePerKg')}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(contract.base_rate_per_kg || contract.baseRatePerKg)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        bgcolor: '#ffffff',
                        border: '1px solid #eef5f7',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {t('myPricing.baseDiscount')}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        {contract.base_discount_percent || contract.baseDiscountPercent}%{' '}
                        {t('myPricing.off')}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        bgcolor: '#ffffff',
                        border: '1px solid #eef5f7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('myPricing.distanceTapering')}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {contract.apply_distance_tapering !== false
                            ? t('myPricing.enabled')
                            : t('myPricing.disabled')}
                        </Typography>
                      </Box>
                      <Chip
                        label={
                          contract.apply_distance_tapering !== false
                            ? t('myPricing.enabled')
                            : t('myPricing.disabled')
                        }
                        size="small"
                        color={contract.apply_distance_tapering !== false ? 'success' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  </Grid>
                </Grid>

                {/* Commitment Info */}
                {(contract.min_quantity_commitment || contract.minQuantityCommitment) > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      {t('myPricing.monthlyCommitment')}
                    </Typography>
                    <Alert variant="outlined" severity="info" sx={{ bgcolor: '#f8fbff' }}>
                      Deliver at least{' '}
                      <strong>
                        {contract.min_quantity_commitment || contract.minQuantityCommitment}
                      </strong>{' '}
                      items per{' '}
                      {(
                        contract.commitment_period ||
                        contract.commitmentPeriod ||
                        'month'
                      ).toLowerCase()}{' '}
                      to avail discounts.
                    </Alert>
                  </Box>
                )}
              </Paper>
            </Box>

            <Box
              sx={{
                flex: { xs: 1, md: '0 0 calc(33.333% - 12px)' },
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: { md: 'flex-end' },
              }}
            >
              <Box
                sx={{
                  width: { xs: '100%', md: 320 },
                  position: { md: 'sticky' },
                  top: { md: 120 },
                }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    color: 'white',
                    background: 'linear-gradient(135deg, #5b6df6 0%, #8f5cf6 100%)',
                    width: '100%',
                  }}
                >
                  <Typography variant="overline" sx={{ opacity: 0.95 }}>
                    Estimated Savings
                  </Typography>

                  {/* If metrics available show original struck, saved amount and % */}
                  {businessMetrics ? (
                    (() => {
                      const totalSavings = parseFloat(
                        businessMetrics.total_savings || businessMetrics.totalSavings || 0
                      );
                      const totalSpend = parseFloat(
                        businessMetrics.total_spend || businessMetrics.totalSpend || 0
                      );
                      const traditional = totalSpend + totalSavings; // original cost without our discounts
                      const pct =
                        traditional > 0 ? Math.round((totalSavings / traditional) * 100) : 0;

                      return (
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'baseline',
                              gap: 2,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                opacity: 0.8,
                                textDecoration: 'line-through',
                              }}
                            >
                              {formatCurrency(traditional)}
                            </Typography>
                            <Typography
                              variant="h3"
                              fontWeight={800}
                              color="#fff"
                              sx={{ my: 1, letterSpacing: '-0.02em' }}
                            >
                              {formatCurrency(totalSavings)}
                            </Typography>
                            <Chip
                              label={`${pct}%`}
                              size="small"
                              sx={{
                                bgcolor: '#ffd54f',
                                color: '#4b2b00',
                                fontWeight: 700,
                              }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            You saved {formatCurrency(totalSavings)} vs standard pricing this month
                          </Typography>

                          {/* <Box sx={{ mt: 2 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setShowRawMetrics((v) => !v)}
                            >
                              {showRawMetrics
                                ? 'Hide raw metrics'
                                : 'View raw metrics'}
                            </Button>
                            {showRawMetrics && (
                              <Box
                                component="pre"
                                sx={{
                                  mt: 1,
                                  p: 2,
                                  bgcolor: 'rgba(255,255,255,0.06)',
                                  borderRadius: 1,
                                  overflowX: 'auto',
                                  maxHeight: 240,
                                }}
                              >
                                {JSON.stringify(businessMetrics, null, 2)}
                              </Box>
                            )}
                          </Box> */}
                        </Box>
                      );
                    })()
                  ) : estimateData ? (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ opacity: 0.8, textDecoration: 'line-through' }}
                        >
                          {formatCurrency(
                            estimateData.perShipStandard * estimateData.shipments +
                              estimateData.monthlySavings
                          )}
                        </Typography>
                        <Typography
                          variant="h3"
                          fontWeight={800}
                          sx={{ my: 1, letterSpacing: '-0.02em' }}
                        >
                          {formatCurrency(estimateData.monthlySavings)}
                        </Typography>
                        <Chip
                          label={`${estimateData.pct}%`}
                          size="small"
                          sx={{
                            bgcolor: '#ffd54f',
                            color: '#4b2b00',
                            fontWeight: 700,
                          }}
                        />
                      </Box>

                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                        Estimated: {formatCurrency(estimateData.perShipStandard)} →{' '}
                        {formatCurrency(estimateData.perShipBusiness)} per shipment ×{' '}
                        {estimateData.shipments} shipments
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => computeEstimate(contract)}
                        >
                          Recalculate Estimate
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setShowRawMetrics((v) => !v)}
                        >
                          Toggle raw estimate
                        </Button>
                      </Box>

                      {showRawMetrics && (
                        <Box
                          component="pre"
                          sx={{
                            mt: 1,
                            p: 2,
                            bgcolor: 'rgba(255,255,255,0.06)',
                            borderRadius: 1,
                            overflowX: 'auto',
                            maxHeight: 240,
                          }}
                        >
                          {JSON.stringify(estimateData, null, 2)}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        minHeight: 84,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="h4"
                        fontWeight={700}
                        sx={{ my: 1, letterSpacing: '-0.02em' }}
                      >
                        —
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Savings will appear after your first billing month
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            </Box>
          </Box>
        )}

        {/* Support */}
        <Box sx={{ mt: 4, p: 3, bgcolor: '#f8fafc', borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Need Help?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Have questions about your pricing? Contact our team at{' '}
            <a
              href={`mailto:${brandingConfig.contact.supportEmail}`}
              style={{ color: 'inherit', fontWeight: 700 }}
            >
              {brandingConfig.contact.supportEmail}
            </a>
          </Typography>
        </Box>
      </Box>
    </LayoutContainer>
  );
};

export default MyPricingPage;
